import React, { useEffect, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import axios from "axios";
import {
  PageShell, TabBar, DataTable, StatusPill, Pill,
  LoadingBox, ErrorBox, EmptyBox, InfiniteSentinel,
  useAdminApi, useInfiniteList, fmtDate, fmtBytes,
  ActionButton, IconButton, useConfirmDialog, Field, Modal, Note, Icon,
  DateCell,
} from "./_adminApi";

// Shared helper: render a licence as a link to its detail page.
// Both app-logs and audit-logs expose the UUID (`licence` field) plus a
// convenience `licence_email`. If we have the id, link to the detail page;
// otherwise show the email as plain text so the row doesn't look broken.
function LicenceLink({ id, email, storeName }) {
  if (!id && !email) return <span className="t-muted">—</span>;
  if (!id) return (
    <span className="t-sub" title="This log row has no linked licence (unlicensed client).">
      {email || "(unlicensed)"}
    </span>
  );
  return (
    <Link
      to={`/dashboard/admin/licences/${id}`}
      style={{ color: "var(--brand-600)", textDecoration: "none", fontWeight: 500 }}
      title="Jump to this licence's detail page"
      onClick={(e) => e.stopPropagation()}
    >
      {email || id}
      {storeName && <div className="t-sub">{storeName}</div>}
    </Link>
  );
}

/**
 * App section — everything that doesn't belong to a single licence or user:
 *   • Releases     (publish new desktop versions)
 *   • Bug reports  (triage user-submitted screen recordings)
 *   • Audit logs   (client-posted actions, flat global stream)
 */
export default function AppSection() {
  const location = useLocation();
  const navigate = useNavigate();
  // Tab lives in the URL hash so the browser-back button works correctly
  // when flipping between tabs. Falls back to "releases" on first visit.
  const activeTab = (location.hash || "#releases").replace(/^#/, "");
  const setTab = (t) => navigate({ hash: `#${t}` }, { replace: true });

  return (
    <PageShell
      title="App Administration"
      subtitle="Publish desktop releases, triage bug reports, review client-posted logs and audit history."
    >
      <TabBar
        active={activeTab}
        onChange={setTab}
        tabs={[
          { key: "releases", label: "Releases", icon: Icon.package },
          { key: "bug-reports", label: "Bug Reports", icon: Icon.bug },
          { key: "app-logs", label: "App Logs", icon: Icon.file },
          { key: "audit-logs", label: "Audit Logs", icon: Icon.shield },
        ]}
      />
      {activeTab === "releases" && <ReleasesTab />}
      {activeTab === "bug-reports" && <BugReportsTab />}
      {activeTab === "app-logs" && <AppLogsStreamTab />}
      {activeTab === "audit-logs" && <AuditLogsTab />}
    </PageShell>
  );
}

// ─── Releases ───────────────────────────────────────────────────

function ReleasesTab() {
  const api = useAdminApi();
  const [showPublish, setShowPublish] = useState(false);
  const [editing, setEditing] = useState(null); // release object currently being edited
  // nonce bumps to force the infinite-list hook to reset after any mutation.
  const [nonce, setNonce] = useState(0);
  const refresh = () => setNonce((n) => n + 1);
  const [confirm, confirmModal] = useConfirmDialog();
  const { items, loadMore, loading, err, hasMore, initialized } =
    useInfiniteList(`admin/releases/?_=${nonce}`);

  const toggleActive = async (r) => {
    try {
      await api.patch(`admin/releases/${r.id}/`, { is_active: !r.is_active });
      refresh();
    } catch (e) { alert(e?.response?.data?.detail || e.message || "Failed to toggle."); }
  };

  const deleteRelease = async (r) => {
    const ok = await confirm({
      title: `Delete release ${r.version}?`,
      body: `This removes the release row from the DB.`,
      detail: `The installer file itself stays in S3 and remains accessible to anyone who still has the direct link. If you want the file gone too, remove it from S3 manually afterwards.`,
      confirmLabel: "Delete release",
      destructive: true,
    });
    if (!ok) return;
    try {
      await api.del(`admin/releases/${r.id}/`);
      refresh();
    } catch (e) { alert(e?.response?.data?.detail || e.message || "Delete failed."); }
  };

  return (
    <div className="ad-card">
      <div className="ad-card-head">
        <div style={{ color: "var(--fg-3)", fontSize: 13, fontWeight: 500 }}>Published desktop installers — the "active" one is what clients auto-update to.</div>
        <ActionButton icon={<Icon.cloud />} onClick={() => setShowPublish(true)}>Publish new version</ActionButton>
      </div>
      {err && <div style={{ padding: 16 }}><ErrorBox error={err} /></div>}
      {!initialized && !err && <LoadingBox />}
      {initialized && (
        <>
          <DataTable
            rows={items}
            emptyText="No releases yet. Click Publish new version to upload the first installer."
            columns={[
              { label: "Version", render: (r) => <span className="ver">{r.version}</span> },
              { label: "Active", render: (r) => r.is_active ? <Pill kind="ok" dot>Active</Pill> : <Pill kind="neutral">Inactive</Pill> },
              { label: "Published", render: (r) => <DateCell iso={r.created_at} /> },
              {
                label: "Downloads",
                render: (r) => (
                  <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    {["windows", "mac", "linux", "other"].map((p) =>
                      r[`${p}_download_url`] ? (
                        <a key={p} href={r[`${p}_download_url`]} target="_blank" rel="noreferrer"
                           style={{ color: "var(--brand-600)", fontSize: 12, fontWeight: 600, textDecoration: "none" }}
                           onClick={(e) => e.stopPropagation()}>
                          {p} ↓
                        </a>
                      ) : null
                    )}
                  </div>
                ),
              },
              { label: "Notes", render: (r) => <span className="t-sub" style={{ whiteSpace: "pre-line", maxWidth: 280, display: "inline-block" }}>{r.notes || "—"}</span> },
              {
                label: "Actions", align: "right",
                render: (r) => (
                  <div className="ad-actions">
                    <IconButton tone={r.is_active ? "brand" : "neutral"} onClick={() => toggleActive(r)}
                      title={r.is_active ? "Deactivate (clients stop seeing this as latest)" : "Mark as active"}>
                      <Icon.check />
                    </IconButton>
                    <IconButton onClick={() => setEditing(r)} title="Edit version / notes / URLs"><Icon.edit /></IconButton>
                    <IconButton tone="danger" onClick={() => deleteRelease(r)} title="Delete release row"><Icon.trash /></IconButton>
                  </div>
                ),
              },
            ]}
          />
          <InfiniteSentinel onReached={loadMore} loading={loading} hasMore={hasMore} />
        </>
      )}
      {showPublish && (
        <PublishModal
          onClose={() => setShowPublish(false)}
          onDone={() => { setShowPublish(false); refresh(); }}
        />
      )}
      {editing && (
        <EditReleaseModal
          release={editing}
          onClose={() => setEditing(null)}
          onDone={() => { setEditing(null); refresh(); }}
        />
      )}
      {confirmModal}
    </div>
  );
}

function EditReleaseModal({ release, onClose, onDone }) {
  const api = useAdminApi();
  const [version, setVersion] = useState(release.version);
  const [notes, setNotes] = useState(release.notes || "");
  const [isActive, setIsActive] = useState(!!release.is_active);
  const [winUrl, setWinUrl] = useState(release.windows_download_url || "");
  const [macUrl, setMacUrl] = useState(release.mac_download_url || "");
  const [linuxUrl, setLinuxUrl] = useState(release.linux_download_url || "");
  const [otherUrl, setOtherUrl] = useState(release.other_download_url || "");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const save = async (e) => {
    if (e) e.preventDefault();
    setErr(null); setSaving(true);
    try {
      await api.patch(`admin/releases/${release.id}/`, {
        version: version.trim(),
        notes: notes.trim(),
        is_active: isActive,
        windows_download_url: winUrl.trim(),
        mac_download_url: macUrl.trim(),
        linux_download_url: linuxUrl.trim(),
        other_download_url: otherUrl.trim(),
      });
      onDone();
    } catch (e) { setErr(e); } finally { setSaving(false); }
  };

  return (
    <Modal
      title="Edit release"
      icon={Icon.edit}
      onClose={onClose}
      footer={
        <>
          <button className="ad-btn ghost" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="ad-btn primary" onClick={save} disabled={saving}><Icon.check />{saving ? "Saving…" : "Save changes"}</button>
        </>
      }
    >
      {err && <ErrorBox error={err} />}
      <form onSubmit={save}>
        <div className="form-grid">
          <Field label="Version" hint="Renaming only changes the DB row — not the S3 filename" full>
            <input type="text" value={version} onChange={(e) => setVersion(e.target.value)} className="form-control" required />
          </Field>
          <Field label="Active" hint="Clients auto-update to the active version" full>
            <label style={{ display: "inline-flex", alignItems: "center", gap: 10, cursor: "pointer" }}>
              <button type="button" className={`tgl${isActive ? " on" : ""}`} onClick={() => setIsActive(!isActive)} aria-pressed={isActive} />
              <span style={{ fontSize: 13, color: "var(--fg-2)" }}>{isActive ? "Published — clients see this as latest" : "Hidden — clients won't auto-update to this"}</span>
            </label>
          </Field>
          <Field label="Changelog / notes" full>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={4} className="form-control" />
          </Field>
          <div className="ad-divider" />
          <Field label="Windows URL"><input type="url" value={winUrl} onChange={(e) => setWinUrl(e.target.value)} className="form-control mono" placeholder="https://…" /></Field>
          <Field label="macOS URL"><input type="url" value={macUrl} onChange={(e) => setMacUrl(e.target.value)} className="form-control mono" placeholder="https://…" /></Field>
          <Field label="Linux URL"><input type="url" value={linuxUrl} onChange={(e) => setLinuxUrl(e.target.value)} className="form-control mono" placeholder="https://…" /></Field>
          <Field label="Other URL"><input type="url" value={otherUrl} onChange={(e) => setOtherUrl(e.target.value)} className="form-control mono" placeholder="https://…" /></Field>
        </div>
      </form>
    </Modal>
  );
}

function PublishModal({ onClose, onDone }) {
  const api = useAdminApi();
  const [version, setVersion] = useState("");
  const [platform, setPlatform] = useState("windows");
  const [notes, setNotes] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [err, setErr] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!version.trim()) return setErr(new Error("Version is required."));
    if (!file) return setErr(new Error("Please pick the installer file."));
    setErr(null);
    setUploading(true);
    setProgress(0);
    try {
      const initRes = await api.post("admin/releases/", {
        version: version.trim(),
        platform,
        filename: file.name,
        notes: notes.trim(),
        is_active: isActive,
      });
      const { upload_url: uploadUrl, public_url: publicUrl } = initRes.data || {};
      if (!uploadUrl) throw new Error("Backend did not return an upload URL.");
      // IMPORTANT: do NOT send x-amz-acl here. The backend already
      // includes ACL=public-read in generate_presigned_url Params, which
      // bakes it into the signed query parameters. Sending it again as a
      // request header makes the client Canonical Request diverge from
      // what was signed → S3 returns 400 SignatureDoesNotMatch.
      // Also: no explicit axios timeout so the browser's own stall logic
      // runs (installer uploads of ~100MB on slow uplinks take minutes).
      await axios.put(uploadUrl, file, {
        headers: { "Content-Type": "application/octet-stream" },
        onUploadProgress: (ev) => { if (ev.total) setProgress(Math.round((ev.loaded / ev.total) * 100)); },
        timeout: 0,                 // no axios-side timeout
        maxContentLength: Infinity, // don't truncate large payloads
        maxBodyLength: Infinity,
      });
      alert(`Release ${version} published.\nPublic URL:\n${publicUrl}`);
      onDone();
    } catch (e) { setErr(e); } finally { setUploading(false); }
  };

  return (
    <Modal
      title="Publish new IMS version"
      icon={Icon.cloud}
      onClose={onClose}
      desc="Upload a signed installer. Clients auto-update to the active version on next launch."
      footer={
        <>
          <button className="ad-btn ghost" onClick={onClose} disabled={uploading}>Cancel</button>
          <button className="ad-btn primary" onClick={submit} disabled={uploading || !file || !version.trim()}>
            <Icon.cloud />{uploading ? `Uploading ${progress}%…` : "Publish"}
          </button>
        </>
      }
    >
      {err && <ErrorBox error={err} />}
      <form onSubmit={submit}>
        <div className="form-grid">
          <Field label="Version string" hint="Semver recommended (e.g. 1.4.2)" full>
            <input type="text" value={version} onChange={(e) => setVersion(e.target.value)} placeholder="e.g. 1.3.0" className="form-control" required />
          </Field>
          <Field label="Platform">
            <select value={platform} onChange={(e) => setPlatform(e.target.value)} className="form-control">
              <option value="windows">Windows</option>
              <option value="mac">macOS</option>
              <option value="linux">Linux</option>
              <option value="other">Other</option>
            </select>
          </Field>
          <Field label="Active on publish">
            <label style={{ display: "inline-flex", alignItems: "center", gap: 10, height: 44 }}>
              <button type="button" className={`tgl${isActive ? " on" : ""}`} onClick={() => setIsActive(!isActive)} aria-pressed={isActive} />
              <span style={{ fontSize: 13, color: "var(--fg-2)" }}>{isActive ? "Clients auto-update" : "Hidden"}</span>
            </label>
          </Field>
          <Field label="Installer file" hint=".exe / .dmg / .AppImage / .deb / .zip" full>
            <input type="file" accept=".exe,.dmg,.AppImage,.deb,.zip" onChange={(e) => setFile(e.target.files?.[0] || null)} className="form-control" required />
            {file && <span className="help">{file.name} — {(file.size / (1024 * 1024)).toFixed(1)} MB</span>}
          </Field>
          <Field label="Changelog / notes" full>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={4} className="form-control" placeholder="What changed in this version?" />
          </Field>
          {uploading && (
            <div style={{ gridColumn: "1 / -1" }}>
              <div style={{ height: 6, background: "var(--bg-3)", borderRadius: 99, overflow: "hidden" }}>
                <div style={{ height: 6, background: "var(--brand-500)", width: `${progress}%`, transition: "width 150ms" }} />
              </div>
              <span className="help" style={{ marginTop: 6, display: "block" }}>{progress}% uploaded…</span>
            </div>
          )}
        </div>
      </form>
    </Modal>
  );
}

// ─── Bug reports ────────────────────────────────────────────────

function BugReportsTab() {
  const navigate = useNavigate();
  const [filter, setFilter] = useState("");
  const url = `admin/bug-reports/${filter ? `?status=${filter}` : ""}`;
  const { items, loadMore, loading, err, hasMore, initialized } = useInfiniteList(url);

  return (
    <div className="ad-card">
      <div className="ad-card-head">
        <div style={{ color: "var(--fg-3)", fontSize: 13, fontWeight: 500 }}>Bug reports submitted from the desktop app's "Report issue" dialog.</div>
        <div className="ad-select">
          <select value={filter} onChange={(e) => setFilter(e.target.value)}>
            <option value="">All statuses</option>
            <option value="new">New only</option>
            <option value="in_progress">In progress</option>
            <option value="resolved">Resolved</option>
          </select>
        </div>
      </div>
      {err && <div style={{ padding: 16 }}><ErrorBox error={err} /></div>}
      {!initialized && !err && <LoadingBox />}
      {initialized && (
        <>
          <DataTable
            rows={items}
            emptyText="No bug reports yet."
            onRowClick={(r) => navigate(`/dashboard/admin/bug-reports/${r.id}`)}
            columns={[
              { label: "#", render: (r) => <span className="t-muted mono">#{r.id}</span> },
              { label: "Status", render: (r) => <StatusPill status={r.status} /> },
              { label: "Title", render: (r) => <span className="t-strong" style={{ maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "inline-block" }}>{r.title || "(no title)"}</span> },
              { label: "Reporter", render: (r) => <span className="t-sub">{r.reporter_email || "—"}</span> },
              { label: "Licence", render: (r) => (
                <span onClick={(e) => e.stopPropagation()}>
                  <LicenceLink id={r.licence} email={r.licence_email} storeName={r.licence_store_name} />
                </span>
              ) },
              { label: "App", render: (r) => <span className="mono">{r.app_version || "—"}</span> },
              { label: "Size", align: "right", render: (r) => <span className="t-num">{fmtBytes(r.size_bytes)}</span> },
              { label: "Received", render: (r) => <DateCell iso={r.created_at} /> },
            ]}
          />
          <InfiniteSentinel onReached={loadMore} loading={loading} hasMore={hasMore} />
        </>
      )}
    </div>
  );
}

// ─── App log stream (global, forwarded from desktop apps) ───────

function AppLogsStreamTab() {
  const [level, setLevel] = useState("error");
  const [q, setQ] = useState("");
  const params = new URLSearchParams();
  if (level) params.set("level", level);
  if (q) params.set("q", q);
  const url = `admin/app-logs/${params.toString() ? `?${params}` : ""}`;
  const { items, loadMore, loading, err, hasMore, initialized } = useInfiniteList(url);

  return (
    <div className="ad-card">
      <div className="ad-card-head">
        <div className="ad-tools" style={{ flex: 1 }}>
          <label className="ad-search">
            <Icon.search />
            <input placeholder="Search message…" value={q} onChange={(e) => setQ(e.target.value)} />
          </label>
          <div className="ad-select">
            <select value={level} onChange={(e) => setLevel(e.target.value)}>
              <option value="">All levels</option>
              <option value="error">Errors only (default)</option>
              <option value="warn">Warnings</option>
              <option value="info">Info</option>
            </select>
          </div>
        </div>
      </div>
      {err && <div style={{ padding: 16 }}><ErrorBox error={err} /></div>}
      {!initialized && !err && <LoadingBox />}
      {initialized && (
        <>
          <DataTable
            rows={items}
            emptyText="No forwarded logs yet. Desktop builds v1.4.0+ forward automatically."
            columns={[
              { label: "When", render: (r) => <DateCell iso={r.occurred_at || r.created_at} /> },
              { label: "Level", render: (r) => (
                <Pill kind={r.level === "error" ? "bad" : r.level === "warn" ? "warn" : "blue"}>{r.level}</Pill>
              ) },
              { label: "Licence", render: (r) => <LicenceLink id={r.licence} email={r.licence_email} /> },
              { label: "Source", render: (r) => <span className="mono">{r.source || "—"}</span> },
              { label: "Message", render: (r) => (
                <pre style={{ fontSize: 12, color: "var(--fg-1)", whiteSpace: "pre-wrap", maxWidth: 480, maxHeight: 120, overflow: "auto", margin: 0, fontFamily: "var(--font-mono)" }}>{r.message}</pre>
              ) },
              { label: "App", render: (r) => <span className="mono t-muted">{r.app_version || "—"}</span> },
            ]}
          />
          <InfiniteSentinel onReached={loadMore} loading={loading} hasMore={hasMore} />
        </>
      )}
    </div>
  );
}

// ─── App audit logs (global) ────────────────────────────────────

function AuditLogsTab() {
  const [filter, setFilter] = useState("");
  const url = `admin/audit-logs/${filter ? `?action=${encodeURIComponent(filter)}` : ""}`;
  const { items, loadMore, loading, err, hasMore, initialized } = useInfiniteList(url);

  return (
    <div className="ad-card">
      <div className="ad-card-head">
        <div className="ad-tools" style={{ flex: 1 }}>
          <label className="ad-search">
            <Icon.search />
            <input placeholder="Filter by action (backup, restore, …)" value={filter} onChange={(e) => setFilter(e.target.value)} />
          </label>
        </div>
      </div>
      {err && <div style={{ padding: 16 }}><ErrorBox error={err} /></div>}
      {!initialized && !err && <LoadingBox />}
      {initialized && (
        <>
          <DataTable
            rows={items}
            emptyText="No audit entries recorded yet."
            columns={[
              { label: "When", render: (r) => <DateCell iso={r.timestamp} /> },
              { label: "Action", render: (r) => <Pill kind="teal">{r.action}</Pill> },
              { label: "Endpoint", render: (r) => <span className="code-pill">{r.endpoint || "—"}</span> },
              { label: "Licence", render: (r) => <LicenceLink id={r.licence} email={r.licence_email} /> },
            ]}
          />
          <InfiniteSentinel onReached={loadMore} loading={loading} hasMore={hasMore} />
        </>
      )}
    </div>
  );
}
